import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Component } from '@/types';
import { shouldBlockSensorControl, useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { useDeviceStatus } from '@/hooks/useDeviceStatus';
import type { DeviceBinding } from '@/components/shared/rendering/InteractionContext';
import { useDataTextValue } from '@/hooks/useDataTextValue';
import { ChartDataSource } from '@/types/chartDataSource';
import { extractHomeAssistantBinding, isHomeAssistantReviewRequired } from '@/services/homeAssistant/binding';
import { executeHomeAssistantAction, getHomeAssistantPlaceholderAction } from '@/services/homeAssistant/executeAction';
import { type HomeAssistantLiveState, useHomeAssistantEntityState } from '@/services/homeAssistant/useEntityState';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DialThemeConfig {
  fillColor: string;
  trackColor: string;
  glowColor: string;
  glowIntensity: number;
  arcThickness: number;
  knobColor: string;
  showKnob: boolean;
  gradient: string[] | null;
  dotMode?: boolean;
  dotGrow?: boolean;
}

interface RadialDialProps {
  component: Component;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const VIEW_SIZE = 200;
const CX = 100;
const CY = 100;
const DEFAULT_OUTER_R = 85;
const DEFAULT_START_ANGLE = 135; // bottom-left (270° sweep, gap at bottom)
const DEFAULT_END_ANGLE = 405;   // bottom-right (135 + 270)
const CENTER_RADIUS = 35;

// ─── Theme Presets ───────────────────────────────────────────────────────────

const DIAL_THEMES: Record<string, DialThemeConfig> = {
  'neon-glow': {
    fillColor: '#ff9500',
    trackColor: 'rgba(0, 0, 0, 0.08)',
    glowColor: '#ff9500',
    glowIntensity: 8,
    arcThickness: 8,
    knobColor: '#ffffff',
    showKnob: true,
    gradient: ['#ff6a00', '#ffb800'],
  },
  'gradient-arc': {
    fillColor: '#00e676',
    trackColor: 'rgba(0, 0, 0, 0.06)',
    glowColor: '#00e676',
    glowIntensity: 6,
    arcThickness: 10,
    knobColor: '#00e676',
    showKnob: true,
    gradient: ['#69f0ae', '#00c853'],
  },
  'minimal': {
    fillColor: '#2196f3',
    trackColor: 'rgba(0, 0, 0, 0.1)',
    glowColor: 'transparent',
    glowIntensity: 0,
    arcThickness: 6,
    knobColor: '#2196f3',
    showKnob: false,
    gradient: null,
  },
'dot-ring': {
    fillColor: '#448aff',
    trackColor: 'rgba(0, 0, 0, 0.08)',
    glowColor: '#448aff',
    glowIntensity: 10,
    arcThickness: 4,
    knobColor: '#448aff',
    showKnob: false,
    gradient: null,
    dotMode: true,
    dotGrow: true,
  },
  'dot-ring-static': {
    fillColor: '#448aff',
    trackColor: 'rgba(0, 0, 0, 0.08)',
    glowColor: '#448aff',
    glowIntensity: 10,
    arcThickness: 4,
    knobColor: '#448aff',
    showKnob: false,
    gradient: null,
    dotMode: true,
  },
  'ambient': {
    fillColor: '#ff7043',
    trackColor: 'rgba(0, 0, 0, 0.08)',
    glowColor: '#ff7043',
    glowIntensity: 15,
    arcThickness: 12,
    knobColor: '#ffffff',
    showKnob: true,
    gradient: ['#ff7043', '#ffca28'],
  },
};

/** Derive a two-stop gradient from a single CSS color (lighter → darker). */
function deriveGradient(color: string): [string, string] {
  // Create an offscreen canvas to resolve any CSS color string to RGB
  const ctx = document.createElement('canvas').getContext('2d')!;
  ctx.fillStyle = color;
  const hex = ctx.fillStyle; // normalised to #rrggbb
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Lighter stop: blend 30% toward white
  const light = (c: number) => Math.min(255, Math.round(c + (255 - c) * 0.3));
  // Darker stop: scale down 25%
  const dark = (c: number) => Math.round(c * 0.75);
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return [
    `#${toHex(light(r))}${toHex(light(g))}${toHex(light(b))}`,
    `#${toHex(dark(r))}${toHex(dark(g))}${toHex(dark(b))}`,
  ];
}

// ─── SVG Geometry Helpers ────────────────────────────────────────────────────
// Adapted from GaugeChart.tsx for standard SVG coordinates.
// Convention: 0° = 3 o'clock (right), angles increase clockwise.
// GaugeChart uses y-up (cy - r*sin) for semicircle; here we use y-down (cy + r*sin).

function polarToCartesian(
  cx: number, cy: number, r: number, angleDeg: number,
): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

/**
 * Build SVG path for an annular (ring) arc segment.
 * Outer arc clockwise, inner arc counter-clockwise, close path.
 */
function describeAnnularArc(
  cx: number, cy: number, innerR: number, outerR: number,
  startAngle: number, endAngle: number,
): string {
  const sweep = endAngle - startAngle;
  if (Math.abs(sweep) < 0.1) return '';
  const largeArc = Math.abs(sweep) > 180 ? 1 : 0;

  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function resolveHomeAssistantValue(
  liveState: HomeAssistantLiveState | null,
  attribute: string | undefined,
  domain: string | undefined,
): number | null {
  if (!liveState) return null;

  if (domain === 'light') {
    const brightness = readNumber(liveState.attributes.brightness);
    if (brightness !== null) {
      return Math.round((Math.max(0, Math.min(255, brightness)) / 255) * 100);
    }
  }

  if (attribute && attribute !== 'state') {
    return readNumber(liveState.attributes[attribute]);
  }

  return readNumber(liveState.state);
}

// ─── Component ───────────────────────────────────────────────────────────────

export const RadialDial: React.FC<RadialDialProps> = ({ component }) => {
  const { properties = {} } = component;
  const svgRef = useRef<SVGSVGElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localValue, setLocalValue] = useState<number | null>(null);
  const rememberedValue = useRef<number>(0);
  const previousExternalValue = useRef<number | null>(null);

  // ─── Extract Properties ──────────────────────────────────────────────────

  const min = (properties.min as number) ?? 0;
  const max = (properties.max as number) ?? 100;
  const step = (properties.step as number) ?? 1;
  const unit = (properties.unit as string) ?? '';
  const themeName = (properties.theme as string) ?? 'neon-glow';
  const isOn = (properties.isOn as boolean) ?? true;
  const offLabel = (properties.offLabel as string) ?? 'OFF';
  const arcStartAngle = (properties.arcStartAngle as number) ?? DEFAULT_START_ANGLE;
  const arcEndAngle = (properties.arcEndAngle as number) ?? DEFAULT_END_ANGLE;
  const decimals = (properties.decimals as number) ?? 1;
  const showRange = (properties.showRange as boolean) ?? false;

  // ─── Theme Resolution (property overrides > theme defaults) ──────────────

  const theme = useMemo(() => {
    const base = DIAL_THEMES[themeName] ?? DIAL_THEMES['neon-glow'];
    return {
      ...base,
      ...(properties.fillColor != null && {
        fillColor: properties.fillColor as string,
        glowColor: properties.fillColor as string,
        knobColor: properties.fillColor as string,
        gradient: base.gradient ? deriveGradient(properties.fillColor as string) : null,
      }),
      ...(properties.trackColor != null && { trackColor: properties.trackColor as string }),
      ...(properties.glowColor != null && { glowColor: properties.glowColor as string }),
      ...(properties.glowIntensity != null && { glowIntensity: properties.glowIntensity as number }),
      ...(properties.arcThickness != null && { arcThickness: properties.arcThickness as number }),
      ...(properties.knobColor != null && { knobColor: properties.knobColor as string }),
      ...(properties.showKnob != null && { showKnob: properties.showKnob as boolean }),
    };
  }, [themeName, properties.fillColor, properties.trackColor, properties.glowColor,
      properties.glowIntensity, properties.arcThickness, properties.knobColor, properties.showKnob]);

  // ─── Data Binding (follows GaugeChart pattern) ───────────────────────────

  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const valueField = (properties.valueField as string) ?? 'value';

  const {
    value: rawValue,
    isLoading,
  } = useDataTextValue({
    dataSource,
    componentId: component.id,
    valueField,
    staticValue: dataSource ? undefined : (properties.value as string | number | undefined),
    templateId,
  });

  // Device binding — for Kasa dimmers etc., real brightness overrides stored value
  const deviceBinding = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceBinding as DeviceBinding | undefined;
    }
    return undefined;
  }, [properties.action]);

  const deviceStatus = useDeviceStatus(deviceBinding);
  const homeAssistantBinding = extractHomeAssistantBinding(properties);
  const homeAssistantAction = getHomeAssistantPlaceholderAction(component);
  const canExecuteHomeAssistantAction = Boolean(
    homeAssistantBinding?.instanceId &&
      homeAssistantAction &&
      !isHomeAssistantReviewRequired(homeAssistantAction.entityId, homeAssistantAction.domain),
  );
  const homeAssistantState = useHomeAssistantEntityState(component);
  const homeAssistantValue = resolveHomeAssistantValue(
    homeAssistantState,
    homeAssistantBinding?.attribute,
    homeAssistantBinding?.domain,
  );

  // Source of truth priority: HA live state > device status > data source > property > min
  const numericValue = homeAssistantValue !== null
    ? homeAssistantValue
    : deviceStatus && typeof deviceStatus.brightness === 'number'
    ? deviceStatus.brightness
    : rawValue !== null
      ? (typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue)) || 0)
      : (properties.value as number) ?? min;

  // Clamp to [min, max]
  const clampedValue = Math.min(max, Math.max(min, numericValue));

  // Local drag state: provides immediate visual feedback during interaction.
  // Keep optimistic HA values visible until HA sends a newer external value.
  const effectiveValue = localValue ?? clampedValue;

  useEffect(() => {
    if (isDragging) return;
    if (previousExternalValue.current === null) {
      previousExternalValue.current = clampedValue;
      return;
    }
    if (previousExternalValue.current !== clampedValue) {
      previousExternalValue.current = clampedValue;
      setLocalValue(null);
    }
  }, [clampedValue, isDragging]);

  // Format display value
  const displayValue = useMemo(() => {
    if (!isOn) return offLabel;
    if (isLoading) return '...';
    return Number.isInteger(effectiveValue)
      ? String(effectiveValue)
      : effectiveValue.toFixed(decimals);
  }, [isOn, isLoading, effectiveValue, offLabel, decimals]);

  // ─── Interaction ─────────────────────────────────────────────────────────

  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';
  const sensorControlBlocked = shouldBlockSensorControl(component);
  const canControl = Boolean(
    (deviceBinding && !sensorControlBlocked) ||
      (isViewerMode && canExecuteHomeAssistantAction) ||
      (isViewerMode && !sensorControlBlocked && handlers.onSensorControl),
  );

  const sensorId = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceId as string || component.id;
    }
    return component.id;
  }, [properties.action, component.id]);

  const sendControl = useCallback(async (value: number) => {
    if (!Number.isFinite(value)) return;

    if (isViewerMode && canExecuteHomeAssistantAction) {
      try {
        await executeHomeAssistantAction(component, {
          value,
          parameters: { command: 'set', property: 'brightness_pct' },
        });
      } catch {
        setLocalValue(null);
      }
      return;
    }

    if (sensorControlBlocked) return;
    if (deviceBinding) {
      const { executeDeviceCommand } = await import('@/integrations/kasa/deviceCommandMapper');
      executeDeviceCommand(
        { sensorId, action: 'control', parameters: { command: 'set', value } },
        deviceBinding
      );
      return;
    }
    if (!isViewerMode || !handlers.onSensorControl) return;
    handlers.onSensorControl({
      sensorId,
      action: 'control',
      parameters: { command: 'set', value },
    });
  }, [
    isViewerMode,
    canExecuteHomeAssistantAction,
    component,
    sensorControlBlocked,
    deviceBinding,
    handlers,
    sensorId,
  ]);

  const handleCenterTap = useCallback(() => {
    if (!canControl) return;
    if (!isOn && !deviceBinding && !canExecuteHomeAssistantAction) return;
    if (effectiveValue > 0) {
      rememberedValue.current = effectiveValue;
      setLocalValue(0);
      sendControl(0);
    } else {
      const restored = rememberedValue.current > 0 ? rememberedValue.current : min;
      setLocalValue(restored);
      sendControl(restored);
    }
  }, [canControl, isOn, deviceBinding, canExecuteHomeAssistantAction, effectiveValue, sendControl, min]);

  const handleValueChange = useCallback((newValue: number) => {
    if (!canControl) return;
    if (!Number.isFinite(newValue)) return;
    const clamped = Math.min(max, Math.max(min, Math.round(newValue / step) * step));
    setLocalValue(clamped);
  }, [canControl, min, max, step]);

  // ─── Arc Geometry ────────────────────────────────────────────────────────

  const totalSweep = arcEndAngle - arcStartAngle;
  const valueRatio = min === max ? 0 : (effectiveValue - min) / (max - min);
  const valueAngle = arcStartAngle + valueRatio * totalSweep;

  const outerR = DEFAULT_OUTER_R;
  const innerR = outerR - theme.arcThickness;

  const trackPath = useMemo(
    () => describeAnnularArc(CX, CY, innerR, outerR, arcStartAngle, arcEndAngle),
    [innerR, outerR, arcStartAngle, arcEndAngle],
  );

  const valuePath = useMemo(
    () => isOn && valueRatio > 0.001
      ? describeAnnularArc(CX, CY, innerR, outerR, arcStartAngle, valueAngle)
      : '',
    [isOn, valueRatio, innerR, outerR, arcStartAngle, valueAngle],
  );

  const knobPos = useMemo(
    () => polarToCartesian(CX, CY, (outerR + innerR) / 2, valueAngle),
    [outerR, innerR, valueAngle],
  );

  // Dot-ring mode: dots along the arc
  const dots = useMemo(() => {
    if (!theme.dotMode) return null;
    const dotCount = 36;
    const dotRadius = theme.arcThickness / 2;
    const midR = (outerR + innerR) / 2;

    return Array.from({ length: dotCount }, (_, i) => {
      const angle = arcStartAngle + (i / (dotCount - 1)) * totalSweep;
      const pos = polarToCartesian(CX, CY, midR, angle);
      const isFilled = isOn && angle <= valueAngle;
      // Optional size growth: filled dots scale from 1.0× to 1.6× based on position
      const progress = theme.dotGrow && isFilled ? i / (dotCount - 1) : 0;
      const r = dotRadius * (1 + progress * 0.6);
      return { x: pos.x, y: pos.y, isFilled, radius: r, key: i };
    });
  }, [theme.dotMode, theme.dotGrow, theme.arcThickness, outerR, innerR,
      arcStartAngle, totalSweep, isOn, valueAngle]);

  // ─── Pointer / Drag Interaction ──────────────────────────────────────────

  const getValueFromPointer = useCallback((clientX: number, clientY: number): number | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;

    const rect = svg.getBoundingClientRect();
    if (!Number.isFinite(rect.width) || !Number.isFinite(rect.height) || rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    const svgX = ((clientX - rect.left) / rect.width) * VIEW_SIZE;
    const svgY = ((clientY - rect.top) / rect.height) * VIEW_SIZE;

    const dx = svgX - CX;
    const dy = svgY - CY;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    if (angle < 0) angle += 360;

    // Map to arc range (handles wrap-around for angles > 360)
    let mapped = angle;
    if (mapped < arcStartAngle) mapped += 360;
    if (mapped < arcStartAngle || mapped > arcEndAngle) return null;

    const ratio = (mapped - arcStartAngle) / totalSweep;
    return min + ratio * (max - min);
  }, [arcStartAngle, arcEndAngle, totalSweep, min, max]);

  const handlePointerDown = useCallback((e: React.PointerEvent<SVGElement>) => {
    if (!canControl || !isOn) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    (e.target as Element).setPointerCapture?.(e.pointerId);

    const value = getValueFromPointer(e.clientX, e.clientY);
    if (value !== null) handleValueChange(value);
  }, [canControl, isOn, getValueFromPointer, handleValueChange]);

  const handlePointerMove = useCallback((e: React.PointerEvent<SVGElement>) => {
    if (!isDragging) return;
    const value = getValueFromPointer(e.clientX, e.clientY);
    if (value !== null) handleValueChange(value);
  }, [isDragging, getValueFromPointer, handleValueChange]);

  const handlePointerUp = useCallback(() => {
    if (isDragging && localValue !== null) {
      sendControl(localValue);
    }
    setIsDragging(false);
  }, [isDragging, localValue, sendControl]);

  // ─── SVG Filter & Gradient IDs ──────────────────────────────────────────

  const filterId = `radial-glow-${component.id}`;
  const gradientId = `radial-gradient-${component.id}`;
  const hasGlow = theme.glowIntensity > 0 && theme.glowColor !== 'transparent';
  const hasGradient = theme.gradient !== null && theme.gradient.length >= 2;
  const fillRef = hasGradient ? `url(#${gradientId})` : theme.fillColor;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="radial-dial"
      className="w-full h-full flex items-center justify-center"
      style={{ minWidth: 80, minHeight: 80 }}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full"
        style={{ maxWidth: '100%', maxHeight: '100%', outline: 'none', overflow: 'visible' }}
        role="slider"
        aria-valuenow={clampedValue}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-label={`${unit ? unit + ' ' : ''}dial: ${displayValue}`}
        tabIndex={canControl ? 0 : undefined}
        onPointerMove={canControl ? handlePointerMove : undefined}
        onPointerUp={canControl ? handlePointerUp : undefined}
        onPointerLeave={canControl ? handlePointerUp : undefined}
      >
        {/* ─── Defs: glow filter + gradient ─── */}
        <defs>
          {hasGlow && (
            <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation={theme.glowIntensity} result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          )}
          {hasGradient && theme.gradient && (
            <linearGradient id={gradientId} x1="0%" y1="50%" x2="100%" y2="50%">
              <stop offset="0%" stopColor={theme.gradient[0]} />
              <stop offset="100%" stopColor={theme.gradient[1]} />
            </linearGradient>
          )}
        </defs>

        {/* ─── Arc rendering ─── */}
        {theme.dotMode && dots ? (
          // Dot-ring mode: individual circles along arc
          <>
            {dots.map((dot) => (
              <circle
                key={dot.key}
                cx={dot.x}
                cy={dot.y}
                r={dot.radius}
                fill={dot.isFilled ? theme.fillColor : theme.trackColor}
                opacity={!isOn && dot.isFilled ? 0.3 : 1}
                filter={dot.isFilled && hasGlow ? `url(#${filterId})` : undefined}
              />
            ))}
          </>
        ) : (
          // Standard arc mode: track + value paths
          <>
            {/* Background track */}
            <path d={trackPath} fill={theme.trackColor} />

            {/* Filled value arc */}
            {valuePath && (
              <path
                d={valuePath}
                fill={fillRef}
                opacity={isOn ? 1 : 0.3}
                filter={hasGlow ? `url(#${filterId})` : undefined}
              />
            )}
          </>
        )}

        {/* ─── Invisible arc interaction overlay ─── */}
        {canControl && isOn && (
          <path
            d={describeAnnularArc(
              CX, CY,
              Math.max(0, innerR - 10),
              Math.min(VIEW_SIZE / 2, outerR + 10),
              arcStartAngle, arcEndAngle,
            )}
            fill="transparent"
            style={{ cursor: isDragging ? 'grabbing' : 'pointer' }}
            onPointerDown={handlePointerDown}
          />
        )}

        {/* ─── Drag knob ─── */}
        {theme.showKnob && isOn && (
          <circle
            data-testid="radial-dial-knob"
            cx={knobPos.x}
            cy={knobPos.y}
            r={theme.arcThickness / 2 + 3}
            fill={theme.knobColor}
            stroke="rgba(0,0,0,0.2)"
            strokeWidth={1}
            style={{ cursor: canControl ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            onPointerDown={canControl ? handlePointerDown : undefined}
            filter={hasGlow ? `url(#${filterId})` : undefined}
          />
        )}

        {/* ─── Center value display ─── */}
        <text
          x={CX}
          y={isOn && unit ? CY - 4 : CY}
          textAnchor="middle"
          dominantBaseline="central"
          fill={isOn ? theme.fillColor : 'rgba(255,255,255,0.4)'}
          fontSize={isOn ? 28 : 20}
          fontWeight="bold"
          fontFamily="system-ui, -apple-system, sans-serif"
          pointerEvents="none"
        >
          {displayValue}
        </text>

        {/* ─── Unit label ─── */}
        {isOn && unit && !isLoading && (
          <text
            x={CX}
            y={CY + 18}
            textAnchor="middle"
            dominantBaseline="central"
            fill="rgba(255,255,255,0.6)"
            fontSize={12}
            fontFamily="system-ui, -apple-system, sans-serif"
            pointerEvents="none"
          >
            {unit}
          </text>
        )}

        {/* ─── Center tap target (on top so it catches clicks) ─── */}
        <circle
          data-testid="radial-dial-center"
          cx={CX}
          cy={CY}
          r={CENTER_RADIUS}
          fill="transparent"
          style={{ cursor: canControl ? 'pointer' : 'default' }}
          onClick={canControl ? handleCenterTap : undefined}
        />

        {/* ─── Loading spinner ─── */}
        {isLoading && (
          <circle
            cx={CX}
            cy={CY}
            r={CENTER_RADIUS + 2}
            fill="none"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth={1}
            strokeDasharray="8 4"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`0 ${CX} ${CY}`}
              to={`360 ${CX} ${CY}`}
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
        )}

        {/* ─── Min/Max labels ─── */}
        {isOn && showRange && (
          <>
            <text
              x={polarToCartesian(CX, CY, outerR + 12, arcStartAngle).x}
              y={polarToCartesian(CX, CY, outerR + 12, arcStartAngle).y}
              textAnchor="middle"
              dominantBaseline="central"
              fill="rgba(255,255,255,0.3)"
              fontSize={10}
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {min}
            </text>
            <text
              x={polarToCartesian(CX, CY, outerR + 12, arcEndAngle).x}
              y={polarToCartesian(CX, CY, outerR + 12, arcEndAngle).y}
              textAnchor="middle"
              dominantBaseline="central"
              fill="rgba(255,255,255,0.3)"
              fontSize={10}
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {max}
            </text>
          </>
        )}
      </svg>
    </div>
  );
};
